import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Center,
    Flex,
    Icon,
    IconButton,
    Text,
    Spinner,
    useDisclosure,
    ModalFooter,
    useToast,
    Badge,
} from '@chakra-ui/react';
import { Column, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import { DITable } from 'plugins/DITable';
import {
    useGetCompanyGroupQuery,
    useGetToolsDetailsQuery,
    useDeleteCompanyGroupMutation,
    useGetToolsQuery,
} from 'core/API/usersApi';
import { tableToolbarStyle, iconTableStyle } from 'shared/styles';
import GlobalFilter from 'plugins/DITable/filters/GlobalFilter';
import icons from 'shared/icons';
import TablePagination from 'plugins/DITable/Pagination';
import {
    COMPANY_HIDDEN_COLUMN,
    COMPANY_FIELDS,
    PREFERED_CG_COLUMN_ORDER,
    HEADER_COMPANY,
} from 'pages/UserManagement/const';
import { ICompanyGroups } from 'pages/UserManagement/types';
import { isFetchBaseQueryError } from 'utils/erros';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { useTranslation } from 'helpers/languages/translations';

import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import MCompanyGroupEdit from '../MCompanyGroupEdit';
import ALinkCell from '../../atoms/ALinkCell';
import AToolsListCell from '../../atoms/AToolsListCell';

const initialState = {
    hiddenColumns: [...COMPANY_HIDDEN_COLUMN],
};

const MCompanyGroupViewer = () => {
    const {
        data: userCompanyGroupsData,
        isLoading: isGetCompanyGroupLoading,
        isError: isGetCompanyGroupError,
    } = useGetCompanyGroupQuery();

    const {
        data: userToolsDetailsData,
        isLoading: isGetToolsDetailsLoading,
        isError: isGetToolsDetailsError,
    } = useGetToolsDetailsQuery();

    const { data: userToolsData, isLoading: isGetToolsLoading, isError: isGetToolsError } = useGetToolsQuery();

    const [deleteCompanyGroupMutation, { isLoading: isDeletedCompanyGroupLoading }] = useDeleteCompanyGroupMutation();
    const [defaultCompanyGroup, setDefaultCompanyGroup] = useState<number | null>(null);
    const { isOpen: isEditModalOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const { isOpen: isDeleteBulkOpen, onOpen: onOpenBulkDelete, onClose: onCloseBulkDelete } = useDisclosure();
    const toast = useToast();

    useEffect(() => {
        if (!isGetCompanyGroupLoading && !isGetCompanyGroupError && userCompanyGroupsData) {
            const defaultCompanyGroup = userCompanyGroupsData?.companyGroup.find((cg) => cg.is_default);
            if (defaultCompanyGroup) {
                const defaultCompanyGroupId = defaultCompanyGroup.id;
                setDefaultCompanyGroup(defaultCompanyGroupId);
            }
        }
    }, [userCompanyGroupsData]);

    const [selectedCompanyGroupList, setSelectedCompanyGroupData] = useState<ICompanyGroups[]>([]);
    const [companyGroupData, setCompanyGroupData] = useState<ICompanyGroups>();
    const [isAdd, setIsAdd] = useState<boolean>(true);
    const translate = useTranslation();

    const filtreAndOrderColumns = (tableHeader: string[]) => {
        const authorizedSort = [...PREFERED_CG_COLUMN_ORDER];
        let headerArray = [...tableHeader];
        headerArray = authorizedSort
            .filter((elem) => headerArray.includes(elem))
            .concat(headerArray.filter((elem: string) => !authorizedSort.includes(elem)).sort());

        return headerArray;
    };

    const data: Readonly<ICompanyGroups[]> = useMemo(() => {
        if (!isGetCompanyGroupLoading && !isGetCompanyGroupError && userCompanyGroupsData) {
            return userCompanyGroupsData.companyGroup;
        }
        return [];
    }, [isGetCompanyGroupError, isGetCompanyGroupLoading, userCompanyGroupsData]);

    const openModalAddUserData = () => {
        setIsAdd(true);
        onEditOpen();
    };

    const openModalWithUserData = (companyGroup: ICompanyGroups) => {
        setIsAdd(false);
        setCompanyGroupData(companyGroup);
        onEditOpen();
    };

    const deleteCompanyGroup = async () => {
        try {
            const res = await deleteCompanyGroupMutation({ id: selectedCompanyGroupList.map((company) => company.id) });
            if ('error' in res) {
                const errorObj = res.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as { message: string }).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error(`something wrong occured deleted the company group`);
            }

            setSelectedCompanyGroupData([]);
            toast({
                title: `succesfully deleted the company group`,
                status: 'success',
                isClosable: true,
            });
        } catch {
            toast({
                title: `error deleted the company group`,
                status: 'error',
                isClosable: true,
            });
        }

        onCloseBulkDelete();
    };

    const renderDeleteModal = () => {
        const title = 'Delete list of company group';
        const selectedEansString = selectedCompanyGroupList.map((elem) => elem.name).join(', ');
        const description = `Are you sure that you want to delete this list of company group : ${selectedEansString}`;

        return (
            <Modal isOpen={isDeleteBulkOpen} onClose={onCloseBulkDelete}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text as="h3" mb="16px">
                            {title}
                        </Text>
                        <Center>
                            <Center w="100px" h="100px" borderRadius="100%" border="2px solid red">
                                <Icon as={icons.warning} w={10} h={10} color="red" />
                            </Center>
                        </Center>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontWeight="bold">{description}</Text>
                        <Text>{translate('cg.edit.modal.delete.body')}</Text>
                    </ModalBody>
                    <ModalFooter fontSize="sm" justifyContent="space-between">
                        <Flex flex="1" justifyContent="flex-end">
                            <Button size="sm" variant="outline" mr={3} onClick={onCloseBulkDelete}>
                                {translate('cg.edit.modal.cancel')}
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="blue"
                                bg="blueMain"
                                isLoading={isDeletedCompanyGroupLoading}
                                onClick={intentionalyFloatingPromiseReturn(deleteCompanyGroup)}
                            >
                                {translate('cg.edit.modal.delete.button')}
                            </Button>
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const renderCompanyGroupEditModal = () => {
        return (
            <Modal isCentered isOpen={isEditModalOpen} size="6xl" onClose={onEditClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text as="h3">
                            {translate(`users.${isAdd ? 'add' : 'edit'}`)}
                            {translate('cg.edit.modal.title')}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton mt="2" />
                    <ModalBody fontSize="sm">
                        <MCompanyGroupEdit
                            isAdd={isAdd}
                            values={companyGroupData}
                            toolsList={(userToolsData && userToolsData.tools) || []}
                            toolsDetails={userToolsDetailsData}
                            onClose={onEditClose}
                            defaultCompanyGroup={defaultCompanyGroup}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    };

    // TODO : Create type for column
    const columns: Readonly<Column<any>[]> = useMemo(() => {
        if (!isGetCompanyGroupLoading && !isGetCompanyGroupError && userCompanyGroupsData) {
            const filteredTableHeader: Column<any>[] = filtreAndOrderColumns(COMPANY_FIELDS).map((header: string) => {
                switch (header) {
                    case 'name':
                        return {
                            Header: translate(HEADER_COMPANY[header]) as string,
                            accessor: header,
                            disableFilters: true,
                            Cell: (props: any) => <ALinkCell {...props} linkCTA={openModalWithUserData} />,
                        };
                    case 'tools':
                        return {
                            Header: translate(HEADER_COMPANY[header]) as string,
                            accessor: header,
                            disableFilters: true,
                            Cell: (props: any) => (
                                <AToolsListCell {...props} toolsList={(userToolsData && userToolsData.tools) || []} />
                            ),
                        };
                    case 'is_default':
                        return {
                            Header: translate(HEADER_COMPANY[header]) as string,
                            accessor: header,
                            disableFilters: true,
                            Cell: ({ row: { original } }) => {
                                return (
                                    <Flex>
                                        {original.is_default && (
                                            <Badge colorScheme="blue" marginRight={2}>
                                                Default
                                            </Badge>
                                        )}
                                        <Badge colorScheme="blue" marginRight={2}>
                                            {original.is_custom_cg_sync_with_tools
                                                ? translate('cg.tools.only')
                                                : translate('cg.tools.detailed')}
                                        </Badge>
                                    </Flex>
                                );
                            },
                        };
                    case 'users':
                        return {
                            Header: translate(HEADER_COMPANY[header]) as string,
                            accessor: header,
                            disableFilters: true,
                            Cell: ({ row: { values } }) => {
                                return <Flex>{values && values.users?.length}</Flex>;
                            },
                        };
                    default:
                        return {
                            Header: header,
                            accessor: header,
                            disableFilters: true,
                            minWidth: 200,
                            width: 200,
                        };
                }
            });

            return filteredTableHeader;
        }

        return [];
    }, [isGetCompanyGroupLoading, isGetCompanyGroupError, userCompanyGroupsData, isGetToolsLoading, isGetToolsError]);

    const defaultColumn = useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        [],
    );

    const tableInstance = useTable(
        {
            data,
            columns,
            defaultColumn,
            initialState,
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    const renderTableToolbar = () => {
        const { state, setGlobalFilter } = tableInstance;
        const PlusIcon = icons.plus;

        return (
            <Flex {...tableToolbarStyle} justifyContent="space-between">
                <Flex>
                    <GlobalFilter globalFilter={state.globalFilter} setGlobalFilter={setGlobalFilter} />
                    <IconButton
                        onClick={openModalAddUserData}
                        {...iconTableStyle}
                        aria-label={translate('users.tab.add.users') as string}
                        icon={<Icon as={PlusIcon} />}
                    />
                </Flex>
            </Flex>
        );
    };

    return (
        <Box m={4}>
            {renderTableToolbar()}
            {renderCompanyGroupEditModal()}
            {renderDeleteModal()}
            {!isGetCompanyGroupLoading && !isGetCompanyGroupError ? (
                <>
                    <DITable {...tableInstance} isPagination />
                    <TablePagination tableInstance={tableInstance} />
                </>
            ) : (
                <Center w="100%" h="200px" bg="white" border="1px solid" borderColor="gray.100">
                    <Spinner />
                </Center>
            )}
        </Box>
    );
};

export default MCompanyGroupViewer;
